import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { IndianRupee, ScanLine, Lock } from 'lucide-react';
import { groupCategories } from '../utils/categoryGroups';
import FormPageLayout from '../components/common/FormPageLayout';
import Alert from '../components/common/Alert';
import DateTimeInput from '../components/common/DateTimeInput';
import CalculatorInput from '../components/common/CalculatorInput';
import MobileSelect from '../components/common/MobileSelect';
import AddAccountModal from '../components/account/AddAccountModal';
import AddCategoryModal from '../components/category/AddCategoryModal';
import { getIconComponent } from '../components/common/IconPicker';
import UpiScannerModal from '../components/pay/UpiScannerModal';
import useUpiPayment from '../components/pay/useUpiPayment';
import { UpiPayeeCard, RecentPayeeChips, PayTrustNote, WebPayNote } from '../components/pay/PayWidgets';
import { useCategoriesQuery, useAccountsQuery, useCreateTransaction, useDescriptionSuggestions } from '../hooks/useQueries';
import { formatDate, getCurrentTime } from '../utils/formatters';
import { useConfig } from '../context/ConfigContext';
import { useToast } from '../context/ToastContext';
import { isValidVpa, isAmountLocked, payeeDisplayName, joinUpiNote, guessUpiCategory, canOpenUpiApps, isScanPayAvailable } from '../utils/upi';
import { getRecentPayees, findRecentPayee, rememberPayee } from '../utils/payMemory';

/**
 * Scan & Pay step 2: pay the payee and save the expense once the user
 * confirms. Separate from the transaction form so Add / Edit Transaction
 * stay as they are; the saved expense can be edited there like any other.
 */
export default function ScanPayFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const scannedPayee = location.state?.upiPayee || null;

  const { config, formatAmount } = useConfig();
  const { toast } = useToast();
  const { startPayment, payDialog } = useUpiPayment();

  const { data: categories = [] } = useCategoriesQuery();
  const { data: accounts = [] } = useAccountsQuery();
  const createTransaction = useCreateTransaction();
  const { descriptions = [], topCategories = [], topAccounts = [] } = useDescriptionSuggestions();

  const [form, setForm] = useState(() => ({
    dateTime: { date: formatDate(new Date(), 'input'), time: getCurrentTime() },
    amount: scannedPayee?.amount ? String(scannedPayee.amount) : '',
    // Left for the user: it becomes the UPI note, which should not be the
    // payee's own name.
    description: '',
    category: '',
    account: '',
    notes: ''
  }));
  const [upi, setUpi] = useState(scannedPayee?.vpa || '');
  const [payee, setPayee] = useState(scannedPayee);
  const [recentPayees] = useState(getRecentPayees);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [categoryReason, setCategoryReason] = useState(null);
  const [showDescSuggestions, setShowDescSuggestions] = useState(false);
  const categoryTouched = useRef(false);
  const accountTouched = useRef(false);

  const expenseCategories = categories.filter(c => c.type === 'expense');
  const activeAccounts = accounts.filter(a => !a.isDeleted && a.isActive !== false);

  // Same pickers as Add Transaction: most used first, then grouped.
  const { categoryOptions, categoryGroups } = useMemo(() => {
    const opts = [];
    const groupOrder = [];
    const toOption = (cat, group) => ({
      value: cat._id, label: cat.name,
      icon: getIconComponent(cat.icon), iconColor: cat.color, group
    });
    const mostUsed = topCategories
      .map(name => expenseCategories.find(c => c.name === name))
      .filter(Boolean);
    if (mostUsed.length > 0) {
      groupOrder.push('Most Used');
      mostUsed.forEach(cat => opts.push(toOption(cat, 'Most Used')));
    }
    const used = new Set(mostUsed.map(c => c._id));
    const remaining = expenseCategories.filter(c => !used.has(c._id));
    if (remaining.length > 0) {
      const { groups, categoriesByGroup } = groupCategories(remaining);
      groups.forEach(groupName => {
        groupOrder.push(groupName);
        categoriesByGroup[groupName].forEach(cat => opts.push(toOption(cat, groupName)));
      });
    }
    return { categoryOptions: opts, categoryGroups: groupOrder.length > 0 ? groupOrder : null };
  }, [expenseCategories, topCategories]);

  const accountOptions = useMemo(() => {
    const sorted = [...activeAccounts].sort((a, b) => {
      const aIdx = topAccounts.indexOf(a.name);
      const bIdx = topAccounts.indexOf(b.name);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.name.localeCompare(b.name);
    });
    return sorted.map(acc => ({
      value: acc._id, label: acc.name,
      icon: getIconComponent(acc.icon), iconColor: acc.color
    }));
  }, [activeAccounts, topAccounts]);

  // Past expense descriptions containing what was typed (like Add Transaction).
  const filteredDescSuggestions = useMemo(() => {
    const query = form.description.trim().toLowerCase();
    if (!query) return [];
    return descriptions
      .filter(d => d.description && (!d.type || d.type === 'expense') &&
        d.description.toLowerCase().includes(query) && d.description.toLowerCase() !== query)
      .slice(0, 6);
  }, [form.description, descriptions]);

  const upiVpa = isValidVpa(upi) ? upi.trim() : '';
  const payeeName = useMemo(() => {
    if (!upiVpa) return null;
    const key = upiVpa.toLowerCase();
    if (payee?.vpa?.toLowerCase() === key) return payee.name || null;
    return recentPayees.find(p => p.vpa.toLowerCase() === key)?.name || null;
  }, [upiVpa, payee, recentPayees]);

  // Defaults from past data: the last payment to this UPI ID first, then
  // history, keywords and most used. Never overrides a manual pick.
  useEffect(() => {
    const remembered = upiVpa ? findRecentPayee(upiVpa) : null;
    const updates = {};
    if (!categoryTouched.current && upiVpa) {
      const guess = guessUpiCategory({
        categories: expenseCategories,
        rememberedCategoryId: remembered?.categoryId,
        payeeName,
        note: form.description,
        descriptions,
        topCategories
      });
      if (guess) {
        updates.category = guess.categoryId;
        setCategoryReason({ id: guess.categoryId, reason: guess.reason });
      }
    }
    if (!accountTouched.current) {
      const accountId = remembered?.accountId && activeAccounts.some(a => a._id === remembered.accountId)
        ? remembered.accountId
        : accountOptions[0]?.value;
      if (accountId) updates.account = accountId;
    }
    if (Object.keys(updates).length > 0) setForm(prev => ({ ...prev, ...updates }));
  }, [upiVpa, expenseCategories.length, activeAccounts.length, descriptions.length, topCategories.length, topAccounts.length]);

  if (!isScanPayAvailable(config)) return <Navigate to="/" replace />;

  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  // Picking a past description also fills its category and account.
  const handleDescriptionSelect = (suggestion) => {
    const updates = { description: suggestion.description };
    if (suggestion.categoryId && expenseCategories.some(c => c._id === suggestion.categoryId)) {
      updates.category = suggestion.categoryId;
      categoryTouched.current = true;
      setCategoryReason(null);
    }
    if (suggestion.accountId && activeAccounts.some(a => a._id === suggestion.accountId)) {
      updates.account = suggestion.accountId;
      accountTouched.current = true;
    }
    setForm(prev => ({ ...prev, ...updates }));
    setShowDescSuggestions(false);
  };

  const applyPayee = (req) => {
    setShowScanner(false);
    setUpi(req.vpa);
    setPayee(req);
    setErrors(prev => ({ ...prev, upi: null }));
    setForm(prev => ({
      ...prev,
      amount: req.amount && (isAmountLocked(req) || !prev.amount) ? String(req.amount) : prev.amount,
      description: prev.description
    }));
  };

  const amountLocked = !!payee && isAmountLocked(payee) && payee.vpa === upi.trim();

  const validate = () => {
    const e = {};
    const typed = upi.trim();
    if (!typed) e.upi = 'Enter or scan the UPI ID to pay';
    else if (!isValidVpa(typed)) e.upi = 'Enter a valid UPI ID, e.g. name@okhdfc';
    if (!form.amount || parseFloat(form.amount) <= 0) e.amount = 'Amount must be greater than 0';
    if (!form.category) e.category = 'Please select a category';
    if (!form.account) e.account = 'Please select an account';
    if (!form.dateTime?.date) e.dateTime = 'Date is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePay = async (e) => {
    e?.preventDefault();
    if (saving) return;
    if (!upi.trim()) {
      setShowScanner(true);
      return;
    }
    if (!validate()) return;

    const vpa = upi.trim();
    const base = payee?.vpa === vpa ? payee : { vpa, name: payeeName, amount: null, note: null, qrParams: {} };
    const typedDescription = form.description.trim();
    const description = typedDescription || payeeDisplayName(base);
    const amount = parseFloat(form.amount);
    // UPI note: the user's description only (cleaned in upiQueryString).
    const req = { ...base, amount, note: typedDescription || null };
    const notes = joinUpiNote(form.notes, vpa, req.name);
    const category = categories.find(c => c._id === form.category);
    const account = activeAccounts.find(a => a._id === form.account);

    // Saved when the user picks a UPI app (see useUpiPayment).
    const save = async () => {
      setSaving(true);
      setError(null);
      try {
        await createTransaction.mutateAsync({
          date: form.dateTime.date,
          time: form.dateTime.time,
          description,
          type: 'expense',
          category: form.category,
          account: form.account,
          amount,
          currency: 'INR',
          exchangeRate: 1,
          notes,
          attachment: null
        });
        rememberPayee({ vpa, name: req.name, categoryId: form.category, accountId: form.account });
      } catch (err) {
        setError(err.error || err.response?.data?.message || 'Failed to save the expense');
        throw err;
      } finally {
        setSaving(false);
      }
    };

    const result = await startPayment({
      req,
      amountText: formatAmount(amount),
      detailLine: [category?.name, account?.name, description].filter(Boolean).join(' · '),
      save
    });
    if (result !== 'saved') return;
    toast.success('Expense saved');
    navigate(-1);
  };

  const payAmount = parseFloat(form.amount);
  const payLabel = payAmount > 0 ? `Pay ${formatAmount(payAmount)}` : 'Pay';

  return (
    <>
    <FormPageLayout title="Scan & Pay" maxWidth="max-w-xl">
      <form onSubmit={handlePay} className="space-y-3">
        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}

        {/* Who is being paid */}
        {payee && payee.vpa === upi.trim() ? (
          <UpiPayeeCard payee={payee} onChange={() => { setPayee(null); setUpi(''); setCategoryReason(null); }} />
        ) : (
          <div>
            <label className="text-xs font-medium text-surface-500 mb-1 block">Pay to (UPI ID)</label>
            <div className="relative">
              <input
                type="text"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="off"
                value={upi}
                onChange={(e) => {
                  setUpi(e.target.value);
                  if (errors.upi) setErrors(prev => ({ ...prev, upi: null }));
                }}
                placeholder="UPI ID, e.g. name@okhdfc"
                className={`input pr-12 ${errors.upi ? 'input-error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                aria-label="Scan UPI QR"
                title="Scan UPI QR"
              >
                <ScanLine className="w-4 h-4" />
              </button>
            </div>
            {payeeName && <p className="text-[11px] text-surface-500 mt-1 truncate">{payeeName}</p>}
            {errors.upi && <p className="text-expense-600 text-xs mt-1">{errors.upi}</p>}
            {!upi.trim() && recentPayees.length > 0 && (
              <div className="mt-2">
                <RecentPayeeChips
                  payees={recentPayees}
                  onSelect={(p) => applyPayee({ vpa: p.vpa, name: p.name, amount: null, note: null, qrParams: {} })}
                />
              </div>
            )}
          </div>
        )}

        {/* Amount */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-1 block">Amount (₹)</label>
          {amountLocked ? (
            <>
              <div className="input text-lg font-semibold flex items-center gap-2 bg-surface-50">
                <IndianRupee className="w-4 h-4 text-surface-400" />
                <span className="flex-1">{form.amount}</span>
                <Lock className="w-4 h-4 text-surface-400" />
              </div>
              <p className="text-[11px] text-surface-400 mt-1">Amount is set by the merchant QR</p>
            </>
          ) : (
            <CalculatorInput
              name="amount"
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
              placeholder="0.00"
              className="input text-lg font-semibold"
              icon={IndianRupee}
              error={!!errors.amount}
              autoFocus={!form.amount}
            />
          )}
          {errors.amount && <p className="text-expense-600 text-xs mt-1">{errors.amount}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-1 block">
            Description <span className="text-surface-400 font-normal">(Optional)</span>
          </label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => {
              setField('description', e.target.value);
              setShowDescSuggestions(true);
            }}
            onFocus={() => setShowDescSuggestions(true)}
            onBlur={() => setTimeout(() => setShowDescSuggestions(false), 150)}
            onKeyDown={(e) => e.key === 'Escape' && setShowDescSuggestions(false)}
            placeholder="What was this for?"
            enterKeyHint="next"
            autoComplete="off"
            className="input"
          />
          {showDescSuggestions && filteredDescSuggestions.length > 0 && (
            <div className="mt-1 bg-white border border-surface-200 rounded-xl shadow-lg max-h-36 overflow-y-auto">
              {filteredDescSuggestions.map(d => (
                <button
                  key={d.description}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleDescriptionSelect(d)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-surface-50 flex items-center justify-between gap-2"
                >
                  <span className="truncate">{d.description}</span>
                  {d.category && (
                    <span className="text-[10px] text-surface-400 whitespace-nowrap">{d.category}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category & Account */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs font-medium text-surface-500 mb-1 block">Category</label>
            <MobileSelect
              value={form.category}
              onChange={(value) => {
                categoryTouched.current = true;
                setField('category', value);
              }}
              options={categoryOptions}
              groups={categoryGroups}
              placeholder={expenseCategories.length === 0 ? 'Add category' : 'Select'}
              label="Select Category"
              columns={3}
              size="sm"
              error={!!errors.category}
              showIconInTrigger
              actionButton={{ label: 'Add New', onClick: () => setShowAddCategory(true) }}
            />
            {errors.category && <p className="text-expense-600 text-xs mt-1">{errors.category}</p>}
            {!errors.category && categoryReason?.reason && categoryReason.id === form.category && (
              <p className="text-[10px] text-surface-400 mt-1 truncate" title={categoryReason.reason}>{categoryReason.reason}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-surface-500 mb-1 block">Account</label>
            <MobileSelect
              value={form.account}
              onChange={(value) => {
                accountTouched.current = true;
                setField('account', value);
              }}
              options={accountOptions}
              placeholder={activeAccounts.length === 0 ? 'Add account' : 'Select'}
              label="Select Account"
              columns={2}
              size="sm"
              error={!!errors.account}
              showIconInTrigger
              actionButton={{ label: 'Add New', onClick: () => setShowAddAccount(true) }}
            />
            {errors.account && <p className="text-expense-600 text-xs mt-1">{errors.account}</p>}
          </div>
        </div>

        {/* Date & Time */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-1 block">Date & Time</label>
          <DateTimeInput
            name="dateTime"
            value={form.dateTime}
            onChange={(value) => setField('dateTime', value)}
            error={!!errors.dateTime}
          />
          {errors.dateTime && <p className="text-expense-600 text-xs mt-1">{errors.dateTime}</p>}
        </div>

        {/* Note */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-1 block">Note</label>
          <input
            type="text"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            placeholder="Add note..."
            className="input text-sm"
          />
        </div>

        <div className="space-y-2.5 pt-3">
          <WebPayNote onPhone={canOpenUpiApps()} />
          <button type="submit" className="btn-primary w-full py-3 text-base font-semibold" disabled={saving}>
            {saving ? 'Saving...' : payLabel}
          </button>
          <PayTrustNote />
        </div>
      </form>
    </FormPageLayout>

    {/* Outside the layout, which renders its content twice (phone and desktop) */}
    <UpiScannerModal isOpen={showScanner} onClose={() => setShowScanner(false)} onResult={applyPayee} />
    {payDialog}
    <AddAccountModal
      isOpen={showAddAccount}
      onClose={() => setShowAddAccount(false)}
      onSuccess={(acc) => {
        setShowAddAccount(false);
        if (acc?._id) {
          accountTouched.current = true;
          setField('account', acc._id);
        }
      }}
    />
    <AddCategoryModal
      isOpen={showAddCategory}
      onClose={() => setShowAddCategory(false)}
      onSuccess={(cat) => {
        setShowAddCategory(false);
        if (cat?._id) {
          categoryTouched.current = true;
          setField('category', cat._id);
        }
      }}
    />
    </>
  );
}
